/**
 * Block Palette Panel
 * Persistent left sidebar with clickable block definitions organized by category
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import type { BlockType, BlockContent, BlockStyles } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

export interface BlockDefinition {
  type: BlockType;
  label: string;
  icon: string;
  category: string;
  defaultContent: BlockContent;
  defaultStyles: BlockStyles;
}

interface BlockPalettePanelProps {
  definitions: BlockDefinition[];
  categories: string[];
  onAddBlock: (definition: BlockDefinition) => void;
}

// ============================================================================
// BLOCK ITEM
// ============================================================================

function BlockItem({
  definition,
  onAddBlock,
}: {
  definition: BlockDefinition;
  onAddBlock: (def: BlockDefinition) => void;
}) {
  return (
    <button
      onClick={() => onAddBlock(definition)}
      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-left text-sm transition-colors hover:bg-gray-100 active:bg-blue-50"
    >
      <span className="text-lg flex-shrink-0 w-6 text-center">{definition.icon}</span>
      <span className="text-gray-700 text-xs font-medium">{definition.label}</span>
    </button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function BlockPalettePanel({
  definitions,
  categories,
  onAddBlock,
}: BlockPalettePanelProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories)
  );
  const [searchQuery, setSearchQuery] = useState('');

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const filteredDefinitions = searchQuery
    ? definitions.filter(
        (def) =>
          def.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          def.type.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : definitions;

  const definitionsByCategory = categories.reduce((acc, category) => {
    acc[category] = filteredDefinitions.filter((def) => def.category === category);
    return acc;
  }, {} as Record<string, BlockDefinition[]>);

  return (
    <div className="w-56 bg-white border-r flex flex-col h-full flex-shrink-0">
      <div className="p-3 border-b">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Blocks
        </h3>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search blocks..."
            className="w-full pl-7 pr-2 py-1.5 text-xs border rounded-md"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {searchQuery ? (
          /* Flat list when searching */
          <div className="px-1">
            {filteredDefinitions.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No blocks found</p>
            ) : (
              filteredDefinitions.map((def) => (
                <BlockItem
                  key={def.type}
                  definition={def}
                  onAddBlock={onAddBlock}
                />
              ))
            )}
          </div>
        ) : (
          /* Categorized list */
          categories.map((category) => {
            const defs = definitionsByCategory[category];
            if (!defs || defs.length === 0) return null;
            const isExpanded = expandedCategories.has(category);

            return (
              <div key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50"
                >
                  <span>{category}</span>
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
                {isExpanded && (
                  <div className="px-1 pb-1">
                    {defs.map((def) => (
                      <BlockItem
                        key={def.type}
                        definition={def}
                        onAddBlock={onAddBlock}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
